import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { useAuthenticated, useUserInfo } from '@lib/user-context';

// ---------------------------------------------------------

import { AGENT_BY_ID_QUERY } from '@lib/fragments';
import { graphql } from '@lib/api';
import RenderBlocks from '@lib/cms/renderBlocks';
import FlexibleDataOptimized from '@lib/cms/flexibleDataOptimized';

// ---------------------------------------------------------

import Layout from '@layout/index';
import Container from '@layout/container';
import Button from '@components/button';

// ---------------------------------------------------------

import { container, button_container, agent_headshot } from './styles.module.scss';
import Image from '@components/image';

// ---------------------------------------------------------

// If you reach this page as a new user with a agent id
// then you will be shown a message about being associated with that agent
// Then forwarded over to the signup page with the agent id
// Then you will reach the account page with the agent id in the query string
// Then you will be associated with that agent

// If you are already a user and you reach this page with an agent id
// You will be shown a message about being associated with that agent
// Then you will be forwarded to the account page with the agent id in the query string
// Then you will be associated with that agent

// If you are already a user with an associated agent with an agent id
// Then you will be asked if you would like to change your associated agent
// If you choose to change your associated agent then you will be associated with that agent
// If you choose not to change your associated agent then you will be forwarded to the homepage

// If you reach this page without an agent id without an account
// Then you will be forwarded to the signup page
// If you reach this page without an agent id with an account
// Then you will be forwarded to the account page

// ---------------------------------------------------------

export async function getStaticProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment ?? null;
  const pageData = await FlexibleDataOptimized.fetch('register', preview, previewEnv);

  return {
    props: { pageData, preview }
  };
}

// ---------------------------------------------------------

const RegisterPage = ({ pageData, preview }) => {
  const router = useRouter();

  const [agent, setAgent] = useState();
  const { userInfo } = useUserInfo();
  const { isAuthenticated: isLoggedIn, isLoading: isUserLoading } = useAuthenticated();
  const associatedAgent = userInfo?.associatedAgent ?? userInfo?.agent;
  const accountResolved = isLoggedIn && !isUserLoading;

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (router.query.agentid) {
      const fetchAgentById = async () => {
        const agentData = await graphql(
          gql`
            ${AGENT_BY_ID_QUERY}
          `,
          { agentId: parseInt(router.query.agentid) }
        );

        if (
          typeof agentData?.agentSearch?.items[0] !== 'undefined' &&
          agentData?.agentSearch?.items[0] != null
        ) {
          setAgent(agentData?.agentSearch?.items[0]);
        }
      };

      fetchAgentById();
    } else {
      router.push('/account');
    }
  }, [router, router.isReady, router.query.agentid]);

  useEffect(() => {
    if (!router.isReady || !router.query.agentid || !accountResolved) {
      return;
    }

    if (parseInt(associatedAgent, 10) === parseInt(router.query.agentid, 10)) {
      router.push('/account');
    }
  }, [accountResolved, associatedAgent, router, router.isReady, router.query.agentid]);

  const registerWithAgent = () => {
    if (router.query.agentid && !associatedAgent && !isLoggedIn) {
      const returnTo = encodeURIComponent(`/account?agentid=${router.query.agentid}`);
      // prompt=signup drives the Sign Up tab on the Classic Universal Login
      // page template; screen_hint covers a future New Universal Login switch
      window.location.href = `/auth/login?prompt=signup&screen_hint=signup&returnTo=${returnTo}`;
    } else if (router.query.agentid && isLoggedIn) {
      router.push(`/account?agentid=${agent?.agentId}`);
    }
  };

  return (
    <Layout
      preview={preview}
      meta={{
        description: pageData?.metaDescription,
        image: pageData?.metaImage,
        title: pageData?.metaTitle
      }}
    >
      <Container spacing="large" gridOptions={{ layout: 'single' }} textAlignment="center">
        {agent && (
          <div className={container}>
            <div className={agent_headshot}>
              <Image
                image={{
                  height: 125,
                  src: agent.individualPhotoUrl,
                  width: 165
                }}
              />
            </div>
            <div>
              <h1>Register with {agent?.name}</h1>
              <p>
                Save your favorite searches and listings, add notes, and streamline property tour
                and information requests by registering with {agent?.name} on Sibcycline.com.
              </p>

              <div className={button_container}>
                {associatedAgent ? (
                  <Button onClick={registerWithAgent} label={'Change agent'} />
                ) : (
                  <Button onClick={registerWithAgent} label={`Register with ${agent?.name}`} />
                )}

                {associatedAgent && (
                  <Button
                    theme="text-only"
                    onClick={() => router.push('/')}
                    label={'I do not want to change my associated agent'}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </Container>
      {pageData?.blocksCollection && RenderBlocks(pageData.blocksCollection.items)}
    </Layout>
  );
};

export default RegisterPage;
